import { useState, useCallback } from "react"
import {DollarSign, Percent, Calculator, ToggleLeft, ToggleRight, ChevronDown, ChevronUp, } from 'lucide-react'
import type { LoanInputs } from "@/utils/calculator"

interface CalculatorFormProps {
  onCalculate: (inputs: LoanInputs) => void
}

export default function CalculatorForm({ onCalculate }: CalculatorFormProps) {
  const [landPrice, setLandPrice] = useState<string>("150000")
  const [downPayment, setDownPayment] = useState<string>("20")
  const [downPaymentType, setDownPaymentType] = useState<"dollar" | "percent">("percent")
  const [interestRate, setInterestRate] = useState<string>("7.5")
  const [loanTermYears, setLoanTermYears] = useState<string>("15")
  const [balloonEnabled, setBalloonEnabled] = useState(false)
  const [balloonYear, setBalloonYear] = useState<string>("5")
  const [extraMonthlyPayment, setExtraMonthlyPayment] = useState<string>("0")
  const [showAdvanced, setShowAdvanced] = useState(false)

  const handleCalculate = useCallback(() => {
    onCalculate({
      landPrice: parseFloat(landPrice) || 0,
      downPayment: parseFloat(downPayment) || 0,
      downPaymentType,
      interestRate: parseFloat(interestRate) || 0,
      loanTermYears: parseInt(loanTermYears) || 15,
      balloonEnabled,
      balloonYear: parseInt(balloonYear) || 5,
      extraMonthlyPayment: parseFloat(extraMonthlyPayment) || 0,
    })
  }, [landPrice, downPayment, downPaymentType, interestRate, loanTermYears, balloonEnabled, balloonYear, extraMonthlyPayment, onCalculate])

  const toggleDownPaymentType = () => {
    setDownPaymentType((prev) => (prev === "dollar" ? "percent" : "dollar"))
  }

  const inputClass =
    "w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl text-gray-800 text-lg font-medium focus:outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100 transition-all duration-200 placeholder-gray-400"

  const labelClass = "block text-sm font-semibold text-gray-600 mb-2 uppercase tracking-wide"

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-900 to-emerald-800 px-6 py-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/15 rounded-xl flex items-center justify-center">
            <Calculator className="w-5 h-5 text-amber-300" />
          </div>
          <div>
            <h3 className="text-white font-bold text-lg" style={{ fontFamily: "'DM Serif Display', serif" }}>
              Land Loan Calculator
            </h3>
            <p className="text-emerald-200 text-sm">Calculate your land financing in seconds</p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-5">
        {/* Land Price */}
        <div>
          <label className={labelClass}>Land Price</label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-semibold">$</span>
            <input
              type="number"
              value={landPrice}
              onChange={(e) => setLandPrice(e.target.value)}
              className={`${inputClass} pl-8`}
              placeholder="150,000"
            />
          </div>
        </div>

        {/* Down Payment */}
        <div>
          <label className={labelClass}>Down Payment</label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-semibold">
                {downPaymentType === "dollar" ? "$" : "%"}
              </span>
              <input
                type="number"
                value={downPayment}
                onChange={(e) => setDownPayment(e.target.value)}
                className={`${inputClass} pl-8`}
                placeholder={downPaymentType === "dollar" ? "30,000" : "20"}
              />
            </div>
            <button
              onClick={toggleDownPaymentType}
              className="flex items-center gap-1.5 px-4 py-3 bg-emerald-50 border-2 border-emerald-200 rounded-xl text-emerald-800 font-semibold text-sm hover:bg-emerald-100 transition-colors whitespace-nowrap"
              aria-label="Toggle between dollar and percent"
            >
              {downPaymentType === "dollar" ? (
                <>
                  <DollarSign className="w-4 h-4" /> USD
                </>
              ) : (
                <>
                  <Percent className="w-4 h-4" /> PCT
                </>
              )}
            </button>
          </div>
        </div>

        {/* Interest Rate & Loan Term */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Interest Rate (%)</label>
            <div className="relative">
              <input
                type="number"
                value={interestRate}
                onChange={(e) => setInterestRate(e.target.value)}
                className={inputClass}
                placeholder="7.5"
                step="0.1"
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 font-semibold">%</span>
            </div>
          </div>
          <div>
            <label className={labelClass}>Loan Term (Years)</label>
            <input
              type="number"
              value={loanTermYears}
              onChange={(e) => setLoanTermYears(e.target.value)}
              className={inputClass}
              placeholder="15"
              min="1"
              max="40"
            />
          </div>
        </div>

        {/* Advanced Options Toggle */}
        <button
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="flex items-center gap-2 text-emerald-700 font-semibold text-sm hover:text-emerald-900 transition-colors w-full py-2"
        >
          {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          Advanced Options
        </button>

        {showAdvanced ? (
          <div className="space-y-5 animate-fade-in border-t border-gray-100 pt-5">
            {/* Balloon Payment */}
            <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <span className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                    Balloon Payment
                  </span>
                  <p className="text-xs text-gray-500 mt-0.5">Common in land contracts</p>
                </div>
                <button
                  onClick={() => setBalloonEnabled(!balloonEnabled)}
                  className="text-emerald-700 hover:text-emerald-900 transition-colors"
                  aria-label="Toggle balloon payment"
                >
                  {balloonEnabled ? (
                    <ToggleRight className="w-10 h-10" />
                  ) : (
                    <ToggleLeft className="w-10 h-10 text-gray-400" />
                  )}
                </button>
              </div>
              {balloonEnabled ? (
                <div className="animate-fade-in">
                  <label className={labelClass}>Balloon Due at Year</label>
                  <input
                    type="number"
                    value={balloonYear}
                    onChange={(e) => setBalloonYear(e.target.value)}
                    className={inputClass}
                    placeholder="5"
                    min="1"
                    max={loanTermYears}
                  />
                </div>
              ) : null}
            </div>

            {/* Extra Monthly Payment */}
            <div>
              <label className={labelClass}>Extra Monthly Payment</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-semibold">$</span>
                <input
                  type="number"
                  value={extraMonthlyPayment}
                  onChange={(e) => setExtraMonthlyPayment(e.target.value)}
                  className={`${inputClass} pl-8`}
                  placeholder="0"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1.5">
                Adding extra payments reduces total interest and shortens your loan term.
              </p>
            </div>
          </div>
        ) : null}

        {/* Calculate Button */}
        <button
          onClick={handleCalculate}
          className="w-full py-4 bg-gradient-to-r from-emerald-800 to-emerald-700 text-white font-bold text-lg rounded-xl hover:from-emerald-900 hover:to-emerald-800 transition-all duration-300 shadow-lg shadow-emerald-900/20 hover:shadow-xl hover:shadow-emerald-900/30 active:scale-[0.98] flex items-center justify-center gap-2"
        >
          <Calculator className="w-5 h-5" />
          Calculate Land Payment
        </button>
      </div>
    </div>
  )
}
